<template>
  <div class="page-layout" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
    <!-- 左侧边栏 -->
    <aside class="chat-sidebar">
      <div class="sidebar-header">
        <div class="sidebar-brand" @click="goToChatHome" title="返回实时对答">
          <img src="/logo.jpg" alt="Okaka" class="brand-logo" />
          <span class="brand-name" v-show="!sidebarCollapsed">Okaka</span>
        </div>
        <button
          class="sidebar-toggle"
          type="button"
          @click="toggleSidebar"
          :title="sidebarCollapsed ? '展开侧栏' : '收起侧栏'"
        >
          <i class="fa fa-columns"></i>
        </button>
      </div>

      <div class="sidebar-nav">
        <div
          class="nav-item"
          :class="{ active: isChatHome }"
          data-tooltip="实时对答"
          @click="goToChatHome"
        >
          <i class="fa fa-comments"></i>
          <span v-show="!sidebarCollapsed">实时对答</span>
        </div>
      </div>

      <div class="sidebar-section">
        <div class="section-label" v-show="!sidebarCollapsed">学习</div>
        <div
          class="nav-item"
          :class="{ active: isPanelActive('/student/dashboard') }"
          data-tooltip="汇总"
          @click="goToDashboard"
        >
          <i class="fa fa-th-large"></i>
          <span v-show="!sidebarCollapsed">汇总</span>
        </div>
        <div
          class="nav-item"
          :class="{ active: isPanelActive('/student/planning') }"
          data-tooltip="学习计划"
          @click="goToStudyPlan"
        >
          <i class="fa fa-plus"></i>
          <span v-show="!sidebarCollapsed">学习计划</span>
        </div>
        <div
          class="nav-item"
          :class="{ active: isPanelActive('/student/error-book') }"
          data-tooltip="错题本"
          @click="navigateTo('/student/error-book')"
        >
          <i class="fa fa-book"></i>
          <span v-show="!sidebarCollapsed">错题本</span>
        </div>
        <div
          class="nav-item"
          :class="{ active: isPanelActive('/student/homework-correction') }"
          data-tooltip="作业批改"
          @click="navigateTo('/student/homework-correction')"
        >
          <i class="fa fa-check-square-o"></i>
          <span v-show="!sidebarCollapsed">作业批改</span>
        </div>
      </div>

      <div class="sidebar-section sidebar-recent">
        <div class="section-label" v-show="!sidebarCollapsed">巩固</div>
        <div
          v-for="item in consolidateShortcuts"
          :key="item.path"
          class="nav-item"
          :class="{ active: isPanelActive(item.path) }"
          :data-tooltip="item.title"
          @click="navigateTo(item.path)"
        >
          <i :class="item.icon"></i>
          <span v-show="!sidebarCollapsed">{{ item.title }}</span>
        </div>
        <div
          class="sidebar-toggle-space"
          role="button"
          tabindex="0"
          :title="sidebarCollapsed ? '展开侧栏' : '收起侧栏'"
          @click="toggleSidebar"
          @keydown.enter.prevent="toggleSidebar"
          @keydown.space.prevent="toggleSidebar"
        ></div>
      </div>

      <div class="sidebar-footer">
        <div class="user-block" @click.stop="openAccountPopup">
          <img :src="currentUser.avatar || defaultUserAvatar" alt="avatar" class="user-avatar" />
          <div class="user-meta" v-show="!sidebarCollapsed">
            <div class="user-name">{{ currentUser.username || '未登录' }}</div>
            <div class="user-badge">{{ currentUser.roleText || '学生' }}</div>
          </div>
        </div>
        <div class="settings-wrap" v-show="!sidebarCollapsed" ref="settingsWrap">
          <button
            class="settings-btn"
            type="button"
            title="设置"
            @click.stop="toggleSettingsMenu"
          >
            <i class="fa fa-cog"></i>
          </button>
          <transition name="settings-fade">
            <div v-if="settingsMenuVisible" class="settings-menu" @click.stop>
              <div
                v-for="item in settingsMenuItems"
                :key="item.path"
                class="settings-menu-item"
                @click="handleSettingsMenuClick(item)"
              >
                <i :class="item.icon"></i>
                <span>{{ item.title }}</span>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </aside>

    <!-- 右侧主内容：对话页与功能页互相替换 -->
    <div class="main-stage">
      <keep-alive include="StudentRealTimeAnswers">
        <router-view />
      </keep-alive>
    </div>

    <user-account-popup
      :visible.sync="accountPopupVisible"
      :user="currentUser"
      :default-avatar="defaultUserAvatar"
      @avatar-updated="onAvatarUpdated"
    />
  </div>
</template>

<script>
import UserAccountPopup from '@/components/UserAccountPopup.vue'

export default {
  name: 'StudentLayout',
  components: {
    UserAccountPopup
  },
  data() {
    return {
      sidebarCollapsed: true,
      settingsMenuVisible: false,
      accountPopupVisible: false,
      userInfoVersion: 0,
      defaultUserAvatar: require('@/assets/img/head.png'),
      settingsMenuItems: [
        { title: '设备管理', path: '/student/profile', icon: 'fa fa-laptop' },
        { title: '偏好设置', path: '/student/preferences', icon: 'fa fa-sliders' }
      ],
      consolidateShortcuts: [
        { title: '预习', path: '/student/preview', icon: 'fa fa-bookmark-o' },
        { title: '复习', path: '/student/review', icon: 'fa fa-refresh' },
        { title: '提升', path: '/student/improve', icon: 'fa fa-line-chart' }
      ]
    }
  },
  computed: {
    isChatHome() {
      return this.$route.name === 'student-real-time-answers'
    },
    currentUser() {
      void this.userInfoVersion
      const roleMap = { student: '学生', teacher: '教师', parent: '家长' }
      try {
        const raw = localStorage.getItem('userInfo')
        if (!raw) {
          return { username: '未登录', roleText: '学生', avatar: '', email: '', role: 'student' }
        }
        const parsed = JSON.parse(raw)
        const profile = parsed.studentProfile || {}
        return {
          username: parsed.username || profile.studentName || '未登录',
          roleText: roleMap[parsed.role] || '学生',
          avatar: parsed.avatar || profile.avatarUrl || '',
          email: parsed.email || profile.email || '',
          role: parsed.role || 'student'
        }
      } catch (e) {
        return { username: '未登录', roleText: '学生', avatar: '', email: '', role: 'student' }
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick)
  },
  methods: {
    isPanelActive(path) {
      if (!path) return false
      return this.$route.path === path || this.$route.path.startsWith(path + '/')
    },
    goToChatHome() {
      if (this.$route.name === 'student-real-time-answers') {
        return Promise.resolve()
      }
      return this.$router.push({ name: 'student-real-time-answers' }).catch(() => {})
    },
    navigateTo(path) {
      if (path && this.$route.path !== path) {
        this.$router.push(path)
      }
    },
    goToDashboard() {
      this.navigateTo('/student/dashboard')
    },
    goToStudyPlan() {
      this.navigateTo('/student/planning')
    },
    openAccountPopup() {
      this.accountPopupVisible = true
    },
    onAvatarUpdated() {
      this.userInfoVersion += 1
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
      if (this.sidebarCollapsed) {
        this.closeSettingsMenu()
      }
    },
    toggleSettingsMenu() {
      this.settingsMenuVisible = !this.settingsMenuVisible
    },
    closeSettingsMenu() {
      this.settingsMenuVisible = false
    },
    handleSettingsMenuClick(item) {
      this.closeSettingsMenu()
      if (!item || !item.path) return
      const [path, queryStr] = item.path.split('?')
      if (queryStr) {
        const query = {}
        queryStr.split('&').forEach(pair => {
          const [key, value] = pair.split('=')
          if (key) query[key] = value || ''
        })
        if (this.$route.path !== path || this.$route.query.tab !== query.tab) {
          this.$router.push({ path, query })
        }
        return
      }
      this.navigateTo(path)
    },
    handleDocumentClick(event) {
      if (!this.settingsMenuVisible) return
      const wrap = this.$refs.settingsWrap
      if (wrap && !wrap.contains(event.target)) {
        this.closeSettingsMenu()
      }
    }
  }
}
</script>

<style lang="less" scoped>
.page-layout {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background-color: #fff;
}

.chat-sidebar {
  width: 170px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #1e1f20;
  color: #e3e3e3;
  transition: width 0.2s ease;
  border-right: 1px solid #2a2b2c;
  position: relative;
  z-index: 10;
  overflow: visible;
}

.sidebar-collapsed .chat-sidebar {
  width: 72px;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 14px 12px;
  min-height: 56px;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  cursor: pointer;
}

.brand-logo {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
}

.brand-name {
  font-size: 18px;
  font-weight: 500;
  color: #fff;
  letter-spacing: 0.2px;
}

.sidebar-toggle {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #c4c7c5;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: #2a2b2c;
    color: #fff;
  }
}

.sidebar-nav {
  padding: 4px 10px 8px;
}

.sidebar-section {
  padding: 8px 10px 4px;
}

.section-label {
  padding: 8px 12px 6px;
  font-size: 12px;
  color: #8e918f;
  letter-spacing: 0.3px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 14px;
  height: 40px;
  padding: 0 12px;
  border-radius: 20px;
  cursor: pointer;
  color: #e3e3e3;
  font-size: 14px;
  transition: background 0.15s ease;

  i {
    width: 18px;
    text-align: center;
    font-size: 15px;
    color: #c4c7c5;
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: #2a2b2c;
  }

  &.active {
    background: #333537;
  }
}

.sidebar-collapsed {
  .sidebar-header {
    flex-direction: column;
    gap: 8px;
    padding: 14px 8px;
  }

  .sidebar-nav,
  .sidebar-section,
  .sidebar-footer {
    padding-left: 8px;
    padding-right: 8px;
  }

  .nav-item {
    position: relative;
    justify-content: center;
    padding: 0;
    gap: 0;

    &::after {
      content: attr(data-tooltip);
      position: absolute;
      left: calc(100% + 10px);
      top: 50%;
      transform: translateY(-50%);
      padding: 6px 10px;
      background: #2a2b2c;
      color: #e8eaed;
      font-size: 13px;
      line-height: 1.2;
      white-space: nowrap;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transition: opacity 0.12s ease, visibility 0.12s ease;
      z-index: 200;
    }

    &:hover::after {
      opacity: 1;
      visibility: visible;
    }
  }

  .user-block {
    justify-content: center;
  }
}

.sidebar-recent {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-bottom: 0;
}

.sidebar-toggle-space {
  flex: 1;
  min-height: 24px;
  cursor: pointer;
  outline: none;
}

.sidebar-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px 16px;
  border-top: 1px solid #2a2b2c;
  margin-top: auto;
  overflow: visible;
  position: relative;
}

.user-block {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  cursor: pointer;
  flex: 1;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: #3c4043;
}

.user-meta {
  min-width: 0;
}

.user-name {
  font-size: 14px;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-badge {
  font-size: 12px;
  color: #8e918f;
  margin-top: 2px;
}

.settings-wrap {
  position: static;
  flex-shrink: 0;
}

.settings-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #c4c7c5;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background: #2a2b2c;
    color: #fff;
  }
}

.settings-menu {
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: calc(100% + 8px);
  width: auto;
  padding: 8px 0;
  background: #2a2b2c;
  border-radius: 16px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
  z-index: 100;
  overflow: hidden;
}

.settings-menu-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 18px;
  color: #e8eaed;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s ease;

  i {
    width: 18px;
    text-align: center;
    font-size: 15px;
    color: #e8eaed;
  }

  &:hover {
    background: #3c4043;
  }
}

.settings-fade-enter-active,
.settings-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.settings-fade-enter,
.settings-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

.main-stage {
  display: flex;
  flex: 1;
  min-width: 0;
  height: 100%;
  overflow: auto;
  background: #f7f8fa;

  // 功能子页面铺满右侧主区域
  /deep/ .practice-container,
  /deep/ .practice-error-container,
  /deep/ .student-dashboard,
  /deep/ .error-book,
  /deep/ .homework-correction,
  /deep/ .study-planning,
  /deep/ .user-profile,
  /deep/ .user-preferences,
  /deep/ .chat-container,
  /deep/ .consolidate-page {
    width: 100%;
    min-height: 100%;
    box-sizing: border-box;
  }

  /deep/ .practice-container,
  /deep/ .practice-error-container {
    height: 100% !important;
  }

  /deep/ .chat-container {
    height: 100%;
  }
}
</style>
